import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import { systemAPI } from '../../utils/axios';
import * as S from './Register.style';

const Register = () => {
  const navigate = useNavigate();
  const { register, isLoading, error, clearError, isAuthenticated } = useAuthStore();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    organization: '',
    department: '',
    registrationCode: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [serverStatus, setServerStatus] = useState('checking'); // 'checking', 'online', 'offline'

  // 이미 로그인된 경우 메인 페이지로 리다이렉트
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/upload');
    }
  }, [isAuthenticated, navigate]);

  // 컴포넌트 마운트시 서버 상태 확인
  useEffect(() => {
    checkServerStatus();
  }, []);

  // 에러 클리어
  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  const checkServerStatus = async () => {
    try {
      const result = await systemAPI.healthCheck();
      setServerStatus(result.success ? 'online' : 'offline');
    } catch (error) {
      console.error('[REGISTER]', 'Server health check failed:', error);
      setServerStatus('offline');
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = '이름을 입력해주세요.';
    } else if (formData.name.trim().length < 2) {
      errors.name = '이름은 2자 이상이어야 합니다.';
    } else if (formData.name.trim().length > 50) {
      errors.name = '이름은 50자 이하여야 합니다.';
    }
    
    if (!formData.email.trim()) {
      errors.email = '이메일을 입력해주세요.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = '올바른 이메일 형식을 입력해주세요.';
    }
    
    if (!formData.password.trim()) {
      errors.password = '비밀번호를 입력해주세요.';
    } else if (formData.password.length < 6) {
      errors.password = '비밀번호는 최소 6자 이상이어야 합니다.';
    }
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = '비밀번호가 일치하지 않습니다.';
    }
    
    if (!formData.organization.trim()) {
      errors.organization = '소속 기관을 입력해주세요.';
    }
    
    if (!formData.department.trim()) {
      errors.department = '부서를 입력해주세요.';
    }

    if (!formData.registrationCode.trim()) {
      errors.registrationCode = '인증코드를 입력해주세요.';
    }
    
    if (!agreeTerms) {
      errors.terms = '이용약관에 동의해주세요.';
    }
    
    return errors;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 입력 시 해당 필드 에러 클리어
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    if (error) {
      clearError();
    }
  };

  const handleShowPasswordToggle = (field) => {
    if (field === 'password') {
      setShowPassword(prev => !prev);
    } else if (field === 'confirmPassword') {
      setShowConfirmPassword(prev => !prev);
    }
  };

  const handleAgreeTermsToggle = () => {
    setAgreeTerms(prev => !prev);
    if (validationErrors.terms) {
      setValidationErrors(prev => ({
        ...prev,
        terms: ''
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // 서버 상태 재확인
    if (serverStatus === 'offline') {
      await checkServerStatus();
      if (serverStatus === 'offline') {
        return;
      }
    }

    const result = await register({
      ...formData,
      username: formData.name,
      registration_code: formData.registrationCode  // 🆕 인증코드 전달
    });
    
    if (result.success) {
      if (result.autoLoginFailed) {
        // 회원가입은 성공했지만 자동 로그인 실패
        alert('회원가입이 완료되었습니다. 로그인 페이지로 이동합니다.');
        navigate('/login');
      } else {
        // 회원가입과 자동 로그인 모두 성공
        navigate('/upload');
      }
    }
  };

  const handlePrivacyPolicyClick = (event) => {
    event.preventDefault();
    // 새 탭에서 개인정보처리방침 페이지 열기
    window.open('/privacy-policy', '_blank', 'noopener,noreferrer');
  };

  const handleTermsClick = (event) => {
    event.preventDefault();
    // 이용약관 페이지 (준비 중)
    alert('이용약관 페이지는 준비 중입니다.');
  };


  const getFieldError = (fieldName) => {
    return validationErrors[fieldName] || (error && fieldName === 'general' ? error : '');
  };

  return (
    <S.RegisterContainer>
      <S.RegisterCard>
        <S.RegisterHeader>
          {/* <S.Logo>
            <S.LogoIcon>🏥</S.LogoIcon>
            <S.LogoText>한국병원약사회</S.LogoText>
          </S.Logo> */}
          <S.RegisterTitle>회원가입</S.RegisterTitle>
          <S.RegisterSubtitle>실태조사 관리시스템에 가입하세요</S.RegisterSubtitle>
          
          {/* 서버 상태 표시 */}
          {serverStatus === 'checking' && (
            <S.ServerStatus status="checking">
              서버 상태 확인 중...
            </S.ServerStatus>
          )}
          {serverStatus === 'offline' && (
            <S.ServerStatus status="offline">
              ⚠️ 서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.
              <S.RetryButton onClick={checkServerStatus}>
                다시 시도
              </S.RetryButton>
            </S.ServerStatus>
          )}
          {serverStatus === 'online' && (
            <S.ServerStatus status="online">
              ✅ 서버 연결 상태 양호
            </S.ServerStatus>
          )}
        </S.RegisterHeader>

        <S.RegisterForm onSubmit={handleSubmit}>
          <S.FormRow>
            <S.FormGroup>
              <S.FormLabel>이름</S.FormLabel>
              <S.FormInput
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="이름을 입력하세요"
                hasError={!!getFieldError('name')}
                disabled={serverStatus === 'offline'}
              />
              {getFieldError('name') && (
                <S.FieldError>{getFieldError('name')}</S.FieldError>
              )}
            </S.FormGroup>
          </S.FormRow>

          <S.FormRow>
            <S.FormGroup>
              <S.FormLabel>이메일</S.FormLabel>
              <S.FormInput
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="이메일을 입력하세요"
                hasError={!!getFieldError('email')}
                disabled={serverStatus === 'offline'}
              />
              {getFieldError('email') && (
                <S.FieldError>{getFieldError('email')}</S.FieldError>
              )}
            </S.FormGroup>
          </S.FormRow>

          <S.FormRow>
            <S.FormGroup>
              <S.FormLabel>비밀번호</S.FormLabel>
              <S.PasswordInputWrapper>
                <S.FormInput
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="비밀번호를 입력하세요 (최소 6자)"
                  hasError={!!getFieldError('password')}
                  disabled={serverStatus === 'offline'}
                />
                <S.PasswordToggle 
                  type="button"
                  onClick={() => handleShowPasswordToggle('password')}
                  disabled={serverStatus === 'offline'}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </S.PasswordToggle>
              </S.PasswordInputWrapper>
              {getFieldError('password') && (
                <S.FieldError>{getFieldError('password')}</S.FieldError>
              )}
            </S.FormGroup>
          </S.FormRow>

          <S.FormRow>
            <S.FormGroup>
              <S.FormLabel>비밀번호 확인</S.FormLabel>
              <S.PasswordInputWrapper>
                <S.FormInput
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="비밀번호를 다시 입력하세요"
                  hasError={!!getFieldError('confirmPassword')}
                  disabled={serverStatus === 'offline'}
                />
                <S.PasswordToggle 
                  type="button"
                  onClick={() => handleShowPasswordToggle('confirmPassword')}
                  disabled={serverStatus === 'offline'}
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </S.PasswordToggle>
              </S.PasswordInputWrapper>
              {getFieldError('confirmPassword') && (
                <S.FieldError>{getFieldError('confirmPassword')}</S.FieldError>
              )}
            </S.FormGroup>
          </S.FormRow>

          <S.FormRow>
            <S.FormGroup>
              <S.FormLabel>소속 기관</S.FormLabel>
              <S.FormInput
                type="text"
                name="organization"
                value={formData.organization}
                onChange={handleInputChange}
                placeholder="예: 한국병원약사회, 삼육대학교"
                hasError={!!getFieldError('organization')}
                disabled={serverStatus === 'offline'}
              />
              {getFieldError('organization') && (
                <S.FieldError>{getFieldError('organization')}</S.FieldError>
              )}
            </S.FormGroup>
          </S.FormRow>

          <S.FormRow>
            <S.FormGroup>
              <S.FormLabel>부서</S.FormLabel>
              <S.FormInput
                type="text"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                placeholder="예: 약제부, 약제팀, 사무국, 약학과"
                hasError={!!getFieldError('department')}
                disabled={serverStatus === 'offline'}
              />
              {getFieldError('department') && (
                <S.FieldError>{getFieldError('department')}</S.FieldError>
              )}
            </S.FormGroup>
          </S.FormRow>


          <S.FormRow>
            <S.FormGroup>
              <S.FormLabel>인증코드</S.FormLabel>
              <S.FormInput
                type="text"
                name="registrationCode"
                value={formData.registrationCode}
                onChange={handleInputChange}
                placeholder="관리자로부터 받은 인증코드를 입력하세요"
                hasError={!!getFieldError('registrationCode')}
                disabled={serverStatus === 'offline'}
              />
              {getFieldError('registrationCode') && (
                <S.FieldError>{getFieldError('registrationCode')}</S.FieldError>
              )}
            </S.FormGroup>
          </S.FormRow>


          {error && (
            <S.ErrorMessage>
              {error}
            </S.ErrorMessage>
          )}

          <S.TermsWrapper>
            <S.CheckboxWrapper>
              <S.Checkbox
                type="checkbox"
                checked={agreeTerms}
                onChange={handleAgreeTermsToggle}
                hasError={!!getFieldError('terms')}
                disabled={serverStatus === 'offline'}
              />
              <S.CheckboxLabel>
                <S.TermsLink onClick={handleTermsClick}>
                  이용약관
                </S.TermsLink>
                {' 및 '}
                <S.TermsLink onClick={handlePrivacyPolicyClick}>
                  개인정보처리방침
                </S.TermsLink>
                에 동의합니다
              </S.CheckboxLabel>
            </S.CheckboxWrapper>
            {getFieldError('terms') && (
              <S.FieldError>{getFieldError('terms')}</S.FieldError>
            )}
          </S.TermsWrapper>

          <S.SubmitButton 
            type="submit" 
            disabled={isLoading || serverStatus === 'offline'}
          >
            {isLoading ? '회원가입 중...' : '회원가입'}
          </S.SubmitButton>
        </S.RegisterForm>

        <S.LoginLink>
          이미 계정이 있으신가요? <Link to="/login">로그인</Link>
        </S.LoginLink>
      </S.RegisterCard>
    </S.RegisterContainer>
  );
};

export default Register;